import React, { useState, useRef, Component } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { UsersIcon, TrendingUpIcon, SparklesIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
type PanelDirection = 'left' | 'right';
interface Panel {
  number: string;
  titleKey: string;
  subtitleKey: string;
  descriptionKey: string;
  bg: string;
  textColor: string;
  numColor: string;
  accentColor: string;
  borderColor: string;
  Icon: ComponentType<{
    className?: string;
  }>;
  direction: PanelDirection;
  pattern: string;
}
const panels: Panel[] = [{
  number: '01',
  titleKey: 'mission.panel1.title',
  subtitleKey: 'mission.panel1.subtitle',
  descriptionKey: 'mission.panel1.description',
  bg: 'bg-haa-blue',
  textColor: 'text-white',
  numColor: 'text-white/20',
  accentColor: 'text-haa-skyblue',
  borderColor: 'border-white',
  Icon: UsersIcon,
  direction: 'left',
  pattern: 'from-haa-blue to-blue-900'
}, {
  number: '02',
  titleKey: 'mission.panel2.title',
  subtitleKey: 'mission.panel2.subtitle',
  descriptionKey: 'mission.panel2.description',
  bg: 'bg-haa-midblue',
  textColor: 'text-white',
  numColor: 'text-white/20',
  accentColor: 'text-white',
  borderColor: 'border-white',
  Icon: TrendingUpIcon,
  direction: 'right',
  pattern: 'from-haa-midblue to-haa-navy'
}, {
  number: '03',
  titleKey: 'mission.panel3.title',
  subtitleKey: 'mission.panel3.subtitle',
  descriptionKey: 'mission.panel3.description',
  bg: 'bg-haa-navy',
  textColor: 'text-white',
  numColor: 'text-white/10',
  accentColor: 'text-haa-skyblue',
  borderColor: 'border-haa-skyblue',
  Icon: SparklesIcon,
  direction: 'left',
  pattern: 'from-haa-navy to-blue-950'
}];
export function MissionPanels() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, {
    once: true,
    margin: '-50px'
  });
  const {
    t,
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const [expanded, setExpanded] = useState<string | null>(null);
  const toggle = (num: string) => setExpanded(prev => prev === num ? null : num);
  return <section ref={ref} aria-label="Connect Celebrate Empower" data-id="element-2764">
      <div className="bg-haa-navy/95 py-10 text-center" data-id="element-2765">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-2766">
          <span className="font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2767">
            {isEs ? 'Nuestra Fuerza' : 'Our Strength'}
          </span>
          <h2 className="font-bebas text-5xl sm:text-6xl lg:text-7xl text-white tracking-wider mt-2 leading-none" data-id="element-2768">
            {isEs ? 'Cómo Nos Elevamos' : 'How We Rise'}
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2769">
            <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2770" />
            <div className="w-3 h-3 rounded-full bg-haa-skyblue" data-id="element-2771" />
            <div className="w-12 h-1 bg-haa-midblue rounded-full" data-id="element-2772" />
          </div>
        </motion.div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3" data-id="element-2773">
        {panels.map((panel, i) => <motion.div key={panel.number} initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        ease: [0.16, 1, 0.3, 1],
        delay: i * 0.15
      }} className={`relative overflow-hidden ${panel.bg}`} data-id="element-2774">
            <div className={`absolute inset-0 bg-gradient-to-br ${panel.pattern} opacity-80`} data-id="element-2775" />
            <div className={`absolute right-2 top-1/2 -translate-y-1/2 font-bebas text-[8rem] leading-none ${panel.numColor} select-none pointer-events-none`} aria-hidden="true" data-id="element-2776">
              {panel.number}
            </div>
            <div className="relative z-10 px-6 sm:px-8 py-10 sm:py-12 text-center" data-id="element-2777">
              <motion.div initial={{
            scale: 0,
            rotate: -20
          }} animate={inView ? {
            scale: 1,
            rotate: 0
          } : {}} transition={{
            duration: 0.5,
            delay: i * 0.15 + 0.2
          }} className={`inline-flex items-center justify-center w-12 h-12 rounded-xl border-2 ${panel.borderColor} mb-4`} data-id="element-2778">
                <panel.Icon className={`w-6 h-6 ${panel.textColor}`} data-id="element-2779" />
              </motion.div>
              <h2 className={`font-bebas text-4xl sm:text-5xl ${panel.textColor} leading-none tracking-wider mb-2`} data-id="element-2780">
                {t(panel.titleKey)}
              </h2>
              <div className="w-10 h-0.5 bg-haa-yellow rounded-full mx-auto mb-3" data-id="element-2781" />
              <p className={`font-nunito text-sm leading-relaxed ${panel.textColor} opacity-85`} data-id="element-2782">
                {t(panel.subtitleKey)}
              </p>
              <button onClick={() => toggle(panel.number)} className={`mt-4 inline-flex items-center gap-1 font-nunito text-xs font-semibold uppercase tracking-wider ${panel.textColor} opacity-70 hover:opacity-100 transition-opacity`} data-id="element-2783">
                {expanded === panel.number ? isEs ? 'Leer Menos' : 'Read Less' : isEs ? 'Leer Más' : 'Read More'}
                <motion.span animate={{
              rotate: expanded === panel.number ? 180 : 0
            }} transition={{
              duration: 0.25
            }} data-id="element-2784">
                  <ChevronDownIcon className="w-3.5 h-3.5" data-id="element-2785" />
                </motion.span>
              </button>
              <AnimatePresence data-id="element-2786">
                {expanded === panel.number && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1]
            }} className="overflow-hidden" data-id="element-2787">
                    <p className={`font-nunito text-sm leading-relaxed ${panel.textColor} opacity-80 mt-4 text-left`} data-id="element-2788">
                      {t(panel.descriptionKey)}
                    </p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </motion.div>)}
      </div>
    </section>;
}
export function Mission() {
  const titleRef = useRef<HTMLDivElement>(null);
  const titleInView = useInView(titleRef, {
    once: true
  });
  const {
    t
  } = useLanguage();
  return <section id="mission" aria-labelledby="mission-heading" data-id="element-2789">
      {/* Section Header */}
      <div ref={titleRef} className="bg-haa-navy py-16 text-center" data-id="element-2790">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={titleInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} data-id="element-2791">
          <span className="font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2792">
            {t('mission.label')}
          </span>
          <h2 id="mission-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-white tracking-wider mt-2" data-id="element-2793">
            {t('mission.heading')}
          </h2>
          <div className="flex items-center justify-center gap-3 mt-4" data-id="element-2794">
            <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2795" />
            <div className="w-3 h-3 rounded-full bg-white" data-id="element-2796" />
            <div className="w-12 h-1 bg-haa-skyblue rounded-full" data-id="element-2797" />
          </div>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="font-nunito text-white/80 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mt-8 px-4" data-id="element-2798">
            {t('mission.statement')}
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          scaleX: 0
        }} animate={titleInView ? {
          opacity: 1,
          scaleX: 1
        } : {}} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="flex items-center justify-center gap-3 mt-10" data-id="element-2799">
            <div className="w-12 h-px bg-haa-skyblue/40 rounded-full" data-id="element-2800" />
            <div className="w-2 h-2 rounded-full bg-haa-skyblue/60" data-id="element-2801" />
            <div className="w-12 h-px bg-haa-skyblue/40 rounded-full" data-id="element-2802" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={titleInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="mt-8 px-4" data-id="element-2803">
            <div className="bg-haa-blue/40 border border-haa-skyblue/30 rounded-2xl px-8 py-8 max-w-3xl mx-auto" data-id="element-2804">
              <span className="font-nunito text-haa-skyblue text-sm uppercase tracking-[0.3em] font-semibold" data-id="element-2805">
                {t('mission.vision.label')}
              </span>
              <h3 className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-white tracking-wider mt-2 mb-4" data-id="element-2806">
                {t('mission.vision.heading')}
              </h3>
              <div className="flex items-center justify-center gap-3 mb-6" data-id="element-2807">
                <div className="w-12 h-1 bg-haa-skyblue rounded-full" data-id="element-2808" />
                <div className="w-3 h-3 rounded-full bg-white" data-id="element-2809" />
                <div className="w-12 h-1 bg-haa-blue rounded-full" data-id="element-2810" />
              </div>
              <p className="font-nunito text-white/80 text-lg sm:text-xl leading-relaxed" data-id="element-2811">
                {t('mission.vision.text')}
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}